import { computeAnnualVolumeM3 } from '../../../../../utils/consumptionCalculations.js';
import { computeHotWaterPrice, getEffectiveHotWaterRatio } from '../../calculations/waterPricing.js';
import { computePaybackYears } from '../../calculations/profitability.js';

// Ecobags are a single generic accessory (no product catalog/model choice like aerators or
// shower reducers), so a flat unit price is used instead of a per-model lookup.
export const ECOBAG_UNIT_PRICE_EUR = 5;

// Derives the effective post-ecobag equipment for a WC after inserting an ecobag.
// Mirrors flowReductionCalculations.js's "swap water_value, recompute" mechanic,
// but the substituted value is derived (current - litersRemoved, floored at 0)
// rather than taken directly as a target.
// wcConsumption() ignores water_value for dual_flush toilets and reads
// technical_data.flush_volume_max/min directly instead, so both flush volumes
// must be reduced too — overriding water_value alone would be a no-op for them.
const getEffectiveEquipment = (equipment, litersRemoved) => {
  const td = equipment.technical_data;
  const currentVolume = equipment.water_value ?? td?.flush_volume_max ?? 0;
  const effectiveVolume = Math.max(0, currentVolume - litersRemoved);

  const effectiveTechnicalData = td?.flush_type === 'dual_flush'
    ? {
        ...td,
        flush_volume_max: Math.max(0, (td.flush_volume_max ?? 0) - litersRemoved),
        flush_volume_min: Math.max(0, (td.flush_volume_min ?? 0) - litersRemoved),
      }
    : td;

  return {
    effectiveVolume,
    effectiveEquipment: { ...equipment, water_value: effectiveVolume, technical_data: effectiveTechnicalData },
  };
};

export const getUsagesForEquipment = (equipmentId, usages) =>
  usages.filter(u => u._id_equipement === equipmentId);

/**
 * @param {Object} equipment - a single WC equipment doc
 * @param {Object[]} usages
 * @param {number} litersRemoved - fixed liter volume removed per flush (e.g. 1, 3)
 *
 * Volumes are tracked separately for hot- and cold-water usages, split proportionally
 * by each usage's effective hot-water ratio — generically, WCs are cold-water only
 * in practice, but no assumption is hardcoded.
 */
export const simulateEcobagEquipmentSavings = (equipment, usages, litersRemoved) => {
  const { effectiveVolume, effectiveEquipment } = getEffectiveEquipment(equipment, litersRemoved);
  const equipmentUsages = getUsagesForEquipment(equipment._id, usages);

  let currentM3Cold = 0, currentM3Hot = 0;
  let newM3Cold = 0, newM3Hot = 0;
  let hasNonReducibleUsage = false;

  equipmentUsages.forEach(usage => {
    const ratio = getEffectiveHotWaterRatio({ usage, equipment });
    if (usage.is_known_volume) {
      hasNonReducibleUsage = true;
      const fixedM3 = usage.annual_volume || 0;
      currentM3Hot += fixedM3 * ratio;
      currentM3Cold += fixedM3 * (1 - ratio);
      newM3Hot += fixedM3 * ratio;
      newM3Cold += fixedM3 * (1 - ratio);
      return;
    }
    const current = computeAnnualVolumeM3(equipment, usage);
    const newVal = computeAnnualVolumeM3(effectiveEquipment, usage);
    currentM3Hot += current * ratio;
    currentM3Cold += current * (1 - ratio);
    newM3Hot += newVal * ratio;
    newM3Cold += newVal * (1 - ratio);
  });

  const savingsM3Cold = Math.max(0, currentM3Cold - newM3Cold);
  const savingsM3Hot = Math.max(0, currentM3Hot - newM3Hot);
  const adjustedNewM3Cold = currentM3Cold - savingsM3Cold;
  const adjustedNewM3Hot = currentM3Hot - savingsM3Hot;

  const currentM3 = currentM3Cold + currentM3Hot;
  const newM3 = adjustedNewM3Cold + adjustedNewM3Hot;
  const savingsM3 = savingsM3Cold + savingsM3Hot;
  const savingsPct = currentM3 > 0 ? (savingsM3 / currentM3) * 100 : 0;

  return {
    equipmentId: equipment._id,
    currentM3,
    newM3,
    savingsM3,
    savingsPct,
    currentM3Cold,
    currentM3Hot,
    newM3Cold: adjustedNewM3Cold,
    newM3Hot: adjustedNewM3Hot,
    savingsM3Cold,
    savingsM3Hot,
    hasNonReducibleUsage,
    currentFlow: equipment.water_value,
    targetFlow: effectiveVolume,
  };
};

/**
 * @param {Object[]} equipments - selected WCs (already filtered to non-built-in)
 * @param {Object[]} usages
 * @param {number} litersRemoved - fixed liter volume removed per flush
 * @param {Object|null} waterPricing - { waterPrice, electricityPrice, fuelPrice, waterHeatingType, hotWaterSetpoint, waterHeatingEfficiency }
 */
export const simulateEcobagSavings = ({ equipments, usages, litersRemoved, waterPricing }) => {
  if (!litersRemoved || litersRemoved <= 0 || !equipments || equipments.length === 0) {
    return { ready: false };
  }

  const perEquipment = equipments.map(e => simulateEcobagEquipmentSavings(e, usages, litersRemoved));

  const totalCurrentM3 = perEquipment.reduce((sum, r) => sum + r.currentM3, 0);
  const totalNewM3 = perEquipment.reduce((sum, r) => sum + r.newM3, 0);
  const totalSavingsM3 = totalCurrentM3 - totalNewM3;
  const savingsPct = totalCurrentM3 > 0 ? (totalSavingsM3 / totalCurrentM3) * 100 : 0;

  const totalSavingsM3Cold = perEquipment.reduce((sum, r) => sum + r.savingsM3Cold, 0);
  const totalSavingsM3Hot = perEquipment.reduce((sum, r) => sum + r.savingsM3Hot, 0);

  const coldWaterPrice = waterPricing?.waterPrice ?? null;
  const hotWaterPrice = computeHotWaterPrice({
    waterPrice: coldWaterPrice,
    electricityPrice: waterPricing?.electricityPrice ?? null,
    fuelPrice: waterPricing?.fuelPrice ?? null,
    waterHeatingType: waterPricing?.waterHeatingType ?? null,
    hotWaterSetpoint: waterPricing?.hotWaterSetpoint ?? null,
    waterHeatingEfficiency: waterPricing?.waterHeatingEfficiency ?? null,
  });

  const waterPriceAvailable = coldWaterPrice != null && coldWaterPrice > 0;
  const savingsEurWaterPerYear = waterPriceAvailable ? totalSavingsM3 * coldWaterPrice : null;

  let heatingPriceAvailable;
  let savingsEurHeatingPerYear;
  if (totalSavingsM3Hot <= 0) {
    heatingPriceAvailable = true;
    savingsEurHeatingPerYear = 0;
  } else if (hotWaterPrice != null && coldWaterPrice != null) {
    heatingPriceAvailable = true;
    savingsEurHeatingPerYear = totalSavingsM3Hot * (hotWaterPrice - coldWaterPrice);
  } else {
    heatingPriceAvailable = false;
    savingsEurHeatingPerYear = null;
  }

  const savingsEurTotalPerYear = waterPriceAvailable
    ? savingsEurWaterPerYear + (heatingPriceAvailable ? savingsEurHeatingPerYear : 0)
    : null;

  // Un document équipement peut représenter plusieurs points d'eau physiques :
  // technical_data.nb_equipment (plusieurs équipements identiques dans une pièce)
  // et location.room_multiplier (pièce répétée à l'identique, cf. nb_identical_rooms).
  const totalPhysicalUnits = equipments.reduce((sum, e) => {
    const nbEquipment = e.technical_data?.nb_equipment ?? 1;
    const roomMultiplier = e.location?.room_multiplier ?? 1;
    return sum + nbEquipment * roomMultiplier;
  }, 0);
  const totalInvestmentEur = totalPhysicalUnits * ECOBAG_UNIT_PRICE_EUR;
  const paybackYears = computePaybackYears({
    investmentEur: totalInvestmentEur,
    investmentFullyKnown: true,
    savingsEurPerYear: savingsEurTotalPerYear,
  });

  return {
    ready: true,
    perEquipment,
    totalInvestmentEur,
    investmentFullyKnown: true,
    paybackYears,
    totalCurrentM3,
    totalNewM3,
    totalSavingsM3,
    totalSavingsM3Cold,
    totalSavingsM3Hot,
    savingsPct,
    waterPriceAvailable,
    heatingPriceAvailable,
    savingsEurWaterPerYear,
    savingsEurHeatingPerYear,
    savingsEurTotalPerYear,
  };
};
